/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Folder model shared by the Gmail and Outlook inboxes.
 *
 * Both providers' folders become `{ key, label, param, kind, unread, depth }`:
 * `param` is what GET /emails/{folder} expects, `kind` drives the icon.
 *
 * Gmail labels keep sending their name (system labels are named after their
 * ids: INBOX, SENT, DRAFT...). Outlook's five standard folders send the names
 * the backend maps to Graph aliases; every other Outlook folder sends its id.
 * Sending the displayName, as before, broke any custom folder and any name
 * with a space.
 */

import {
  Inbox as InboxIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Drafts as DraftsIcon,
  Star as StarIcon,
  Label as LabelIcon,
  Archive as ArchiveIcon,
  Report as ReportIcon,
  Outbox as OutboxIcon,
  Folder as FolderIcon,
  LabelImportant as ImportantIcon,
} from '@mui/icons-material';

const KIND_ICONS = {
  inbox: InboxIcon,
  sent: SendIcon,
  drafts: DraftsIcon,
  trash: DeleteIcon,
  spam: ReportIcon,
  archive: ArchiveIcon,
  starred: StarIcon,
  important: ImportantIcon,
  outbox: OutboxIcon,
};

// Lower-cased names, from both Gmail labels and Outlook displayNames.
const NAME_KINDS = {
  inbox: 'inbox',
  sent: 'sent',
  'sent mail': 'sent',
  'sent items': 'sent',
  sentitems: 'sent',
  draft: 'drafts',
  drafts: 'drafts',
  trash: 'trash',
  'deleted items': 'trash',
  deleteditems: 'trash',
  spam: 'spam',
  junk: 'spam',
  'junk email': 'spam',
  junkemail: 'spam',
  archive: 'archive',
  'all mail': 'archive',
  starred: 'starred',
  important: 'important',
  outbox: 'outbox',
};

const KIND_ORDER = ['inbox', 'starred', 'important', 'drafts', 'sent', 'archive', 'spam', 'trash', 'outbox'];

// Outlook kinds the backend addresses by well-known name rather than by id.
const OUTLOOK_STANDARD_PARAMS = {
  inbox: 'Inbox',
  sent: 'Sent',
  drafts: 'Drafts',
  trash: 'Trash',
  spam: 'Spam',
};

const GMAIL_HIDDEN_LABELS = new Set(['CHAT', 'UNREAD']);

const GMAIL_SYSTEM_LABELS = {
  INBOX: 'Inbox',
  SENT: 'Sent',
  DRAFT: 'Drafts',
  TRASH: 'Trash',
  SPAM: 'Spam',
  STARRED: 'Starred',
  IMPORTANT: 'Important',
};

export const folderKindFromName = (name) => NAME_KINDS[String(name || '').trim().toLowerCase()] || 'custom';

/**
 * Icon for a folder. Accepts a folder model (uses `kind`) or, for older
 * callers, a name plus provider. Outlook names arrive as displayNames
 * ('Sent Items', 'Deleted Items', 'Junk Email'), which the old switch keyed on
 * well-known ids ('sentitems') and so never matched.
 */
export const getFolderIcon = (folderOrName, provider) => {
  if (folderOrName && typeof folderOrName === 'object') {
    return KIND_ICONS[folderOrName.kind] || (folderOrName.depth > 0 || provider === 'Outlook' ? FolderIcon : LabelIcon);
  }
  const kind = folderKindFromName(folderOrName);
  if (KIND_ICONS[kind]) return KIND_ICONS[kind];
  return provider === 'Outlook' ? FolderIcon : LabelIcon;
};

function gmailFolder(label) {
  const id = label.id || label.name;
  const system = GMAIL_SYSTEM_LABELS[id];
  const kind = system ? folderKindFromName(system) : 'custom';
  return {
    key: id,
    label: system || label.name || id,
    param: label.name || id,
    kind,
    unread: label.messagesUnread || 0,
    depth: 0,
  };
}

function outlookFolder(folder) {
  const label = folder.name || folder.displayName || '';
  const kind = folder.kind || folderKindFromName(label);
  return {
    key: folder.id || label,
    label,
    param: OUTLOOK_STANDARD_PARAMS[kind] || folder.id || label,
    kind,
    unread: folder.unread_count ?? folder.unreadItemCount ?? 0,
    depth: folder.depth || 0,
  };
}

const sortRank = (folder) => {
  const index = KIND_ORDER.indexOf(folder.kind);
  return index === -1 ? KIND_ORDER.length : index;
};

/** Folders from GET /emails/folders as display models, in display order. */
export function toFolderModels(rawFolders, provider) {
  const list = Array.isArray(rawFolders) ? rawFolders : [];
  if (provider === 'Outlook') {
    // The backend already orders Outlook folders as a tree (children after
    // their parent); re-sorting would detach children from their parents.
    return list.map(outlookFolder);
  }
  return list
    .filter((label) => {
      const id = label.id || '';
      return !id.startsWith('CATEGORY_') && !GMAIL_HIDDEN_LABELS.has(id);
    })
    .map(gmailFolder)
    .sort((a, b) => sortRank(a) - sortRank(b) || a.label.localeCompare(b.label));
}
